import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { AppConfigService } from '@config/app-config.service';
import { FlickrPhotosetsPhotos } from '@model/flickr';
import { PhotoAlbum } from '@model/photo-album';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from '@shared/constants';
import {
  convertFlickrDate,
  createDefaultFlickrHttpParams,
  createFavoritesAlbum,
  createPhotoAlbum,
} from '@util/photo-util';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { PhotoService } from './photo.service';

@Injectable({ providedIn: 'root' })
export class PhotoAlbumService {
  private appConfigService = inject(AppConfigService);
  private httpClient = inject(HttpClient);
  private photoService = inject(PhotoService);
  private translateService = inject(TranslateService);

  photoAlbums = computed<PhotoAlbum[]>(() => this._photoAlbums());

  favoritePhotoAlbum = computed<PhotoAlbum | undefined>(() => {
    const favoriteAlbum = createFavoritesAlbum(
      this.photoService.favorites(),
      this.translateService.instant('albums.favorites.title') as string,
      this.translateService.instant('albums.favorites.description') as string,
    );
    return favoriteAlbum;
  });

  hasPhotoAlbums = computed<boolean>(() => this.photoAlbums().length > 0);

  private _allPhotosAlbums = computed<PhotoAlbum[]>(() => {
    const photoAlbums = this._photoAlbums();
    const favoritePhotoAlbum = this.favoritePhotoAlbum();

    return [...(favoritePhotoAlbum ? [favoritePhotoAlbum] : []), ...photoAlbums];
  })

  private _photoAlbums = signal<PhotoAlbum[]>([]);

  constructor() {
    timer(
      Constants.PHOTO_ALBUMS_UPDATE_INTERVAL,
      Constants.PHOTO_ALBUMS_UPDATE_INTERVAL,
    )
      .pipe(mergeMap(() => this.loadPhotoAlbums()))
      .subscribe();
  }

  init(): Observable<void> {
    // Get all flickr photo albums from flickr or the local database
    return this.loadPhotoAlbums();
  }

  onResume(): Observable<void> {
    return this.loadPhotoAlbums();
  }

  private loadPhotoAlbums(): Observable<void> {
    const photoAlbumsMinDate =
      this.appConfigService.appConfig()?.photoAlbumsMinDate;
    const photoAlbumsFilterMilliseconds = photoAlbumsMinDate
      ? new Date(photoAlbumsMinDate).getTime()
      : undefined;
    const params = createDefaultFlickrHttpParams(
      Constants.FLICKS_ALBUM_OPERATION,
    ).append('primary_photo_extras', 'date_taken');

    // TODO: error handling
    return this.httpClient
      .get<FlickrPhotosetsPhotos>(Constants.FLICKR_URL, { params })
      .pipe(
        map(flickrPhotosetsPhotos =>
          flickrPhotosetsPhotos.photosets.photoset
            .filter(photoSet =>
              photoAlbumsFilterMilliseconds
                ? convertFlickrDate(
                  photoSet.primary_photo_extras.datetaken,
                ).getTime() >= photoAlbumsFilterMilliseconds
                : true,
            )
            .map(photoSet => createPhotoAlbum(photoSet)),
        ),
        tap(photoAlbums => this._photoAlbums.set(photoAlbums)),
        map(() => undefined),
        catchError(() => {
          // On error of Flickr call the flow needs to continue.
          // Will set _photoAlbums to an empty array
          // The timer in the effect will re-run the load of photo albums
          this._photoAlbums.set([]);
          return of(undefined);
        }),
      );
  }

  getAlbum(uuid?: string): PhotoAlbum | undefined {
    return this._allPhotosAlbums().find(photoAlbum => photoAlbum.uuid === uuid);
  }
}
